import { Page } from '../Elements/PageMargins'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { events, extractedErrorObject } from '../../api/swagger'
import TitleContext from '../../TitleContext'
import EventFormCard from '../Card/EventFormCard'
import { Event } from '../../swagger'
import { useNavigate } from 'react-router'
import { SnackbarSeverity } from '../Alerts/SnackbarAlert'
import DynamicBreadcrumbs from '../Elements/DynamicBreadcrumbs'
import EmptyPage from '../Elements/EmptyPage'
import Typography from '@mui/material/Typography'
import { LoadingContext } from '../Context/LoadingContext'
import { OperationIds } from '../../swagger/operationIdEnum'
import { useMountEffect } from '../../hooks/useMountEffect'
import CircularProgressOverlay from '../Elements/CircularProgressOverlay'
import { useSnackbarContext } from '../Context/SnackbarContext'
import { useNavigationAwayContext } from '../Context/NavigationAwayContext'
import LoadingProgress from '../Elements/LoadingProgress'
import { EventFormCardVariants } from './EventEnums'

const filename = 'EventDetails'

export const EventDetails: React.FunctionComponent = (props) => {
  const { t } = useTranslation()
  const title = t('Events.EventDetails.Header', 'Events')
  const navigate = useNavigate()

  const { setSnackbarState, setSnackbarMessage, setSnackbarSeverity } =
    useSnackbarContext()
  const { updateNavigationInformation, isEditMode, updateActionConfirmed } =
    useNavigationAwayContext()

  const { useTitleEffect } = React.useContext(TitleContext)
  useTitleEffect(title)

  const { eventKey } = useParams<{
    eventKey: string
  }>()

  const { updateInitiateBlock } = useNavigationAwayContext()

  const eventId = parseInt(`${eventKey}`)

  const [eventDetails, setEventDetails] = useState<Event | undefined>(undefined)

  const failedToFetchEventMessage = t(
    'Events.EventDetails.FailedFetchEvent',
    'An unknown error occurred fetching event details.'
  )

  const fetchEventLoadingId = `${filename}${OperationIds.FetchEvent}`
  const { addLoadingIds, removeLoadingIds, loadingIds } =
    React.useContext(LoadingContext)

  useMountEffect(() => {
    addLoadingIds([fetchEventLoadingId])
    const fetchEventDetails = async () => {
      try {
        const fetchedEventDetails = await events.fetchEvent({
          eventKey: eventId,
        })

        setEventDetails(fetchedEventDetails)
      } catch (error) {
        const errorObject = (await extractedErrorObject(error)) ?? {
          code: 'Unknown',
          message:
            (error as unknown as Error).message ?? failedToFetchEventMessage,
        }
        setSnackbarState?.(true)
        setSnackbarMessage?.(errorObject.message)
        setSnackbarSeverity?.(SnackbarSeverity.Error)
      } finally {
        removeLoadingIds([fetchEventLoadingId])
      }
    }
    fetchEventDetails()
  })

  const updateEventName = (eventName: string) => {
    !!eventDetails?.name &&
      setEventDetails({
        ...eventDetails,
        name: eventName,
      })
  }

  const clickNavigateToEvents = () => {
    // Determine blockage on the overrideBlock values antithesis.
    if (isEditMode) {
      updateInitiateBlock(true)
      updateNavigationInformation({ pathname: '/events' })
      updateActionConfirmed(true)
    } else {
      navigate(
        {
          pathname: '/events',
        },
        {
          /** Navigation Options */
        }
      )
    }
  }

  if (loadingIds.has(fetchEventLoadingId)) {
    return <LoadingProgress />
  }

  if (!eventDetails && !loadingIds.has(fetchEventLoadingId)) {
    return (
      <EmptyPage
        message={
          <Typography variant="subtitle1" component="p">
            {t(
              'Events.EmptyState.Message',
              `The event either does not exist or you do not have permission to view it.`
            )}
          </Typography>
        }
      />
    )
  }

  if (loadingIds.has(fetchEventLoadingId)) {
    return (
      <CircularProgressOverlay
        isLoading={loadingIds.has(fetchEventLoadingId)}
      />
    )
  }

  return (
    <Page>
      <DynamicBreadcrumbs
        breadcrumbs={[
          {
            label: t('Events.EventDetails.BreadCrumb', 'Events'),
            onClick: clickNavigateToEvents,
          },
          {
            label: eventDetails?.name,
          },
        ]}
      />
      <EventFormCard
        variant={EventFormCardVariants.EditEvent}
        eventDetails={eventDetails}
        eventKey={eventId}
        updateEventName={updateEventName}
        {...props}
      />
    </Page>
  )
}

export default EventDetails
